import { useState, useEffect } from 'react';
import { polygonChartDataService } from '@/services/api/polygonChartData.service';

interface UseStockSparklinesReturn {
  chartDataMap: Record<string, number[]>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch sparkline chart data for multiple stock tickers
 * Uses Polygon API for fast, reliable intraday data
 * Fetches in parallel for optimal performance
 *
 * @param tickers - Array of stock ticker symbols
 * @returns Object with chartDataMap (ticker -> prices), loading state, and error
 *
 * @example
 * const { chartDataMap, isLoading } = useStockSparklines(['AAPL', 'TSLA', 'NVDA']);
 * // chartDataMap = { 'AAPL': [150, 151, 152, ...], 'TSLA': [200, 205, 210, ...] }
 */
export const useStockSparklines = (tickers: string[]): UseStockSparklinesReturn => {
  const [chartDataMap, setChartDataMap] = useState<Record<string, number[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChartData = async () => {
      if (tickers.length === 0) {
        setChartDataMap({});
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log(
          `[useStockSparklines] Fetching chart data for ${tickers.length} tickers via Polygon`
        );
        const startTime = Date.now();

        const newChartData: Record<string, number[]> = {};

        // Fetch chart data for all tickers in parallel using Polygon API
        await Promise.all(
          tickers.map(async ticker => {
            try {
              // Get 1-day intraday chart data from Polygon
              const result = await polygonChartDataService.getChartData(ticker, '1D');

              if (result.error) {
                console.warn(`[useStockSparklines] ⚠️ Error for ${ticker}: ${result.error}`);
                return;
              }

              if (result.data && result.data.length > 0) {
                // Extract closing prices for sparkline
                const prices = result.data.map(d => d.close);
                newChartData[ticker] = prices;
                console.log(
                  `[useStockSparklines] ✅ Loaded ${prices.length} data points for ${ticker}`
                );
              } else {
                console.warn(`[useStockSparklines] ⚠️ No chart data for ${ticker}`);
              }
            } catch (error) {
              console.error(`[useStockSparklines] ❌ Error fetching chart for ${ticker}:`, error);
              // Don't fail the whole batch for one stock
            }
          })
        );

        const duration = Date.now() - startTime;
        console.log(
          `[useStockSparklines] ✅ Loaded ${Object.keys(newChartData).length}/${tickers.length} charts in ${duration}ms (Polygon)`
        );

        setChartDataMap(newChartData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sparkline data';
        setError(errorMessage);
        console.error('[useStockSparklines] Error:', errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChartData();
  }, [tickers.join(',')]); // Re-fetch when ticker list changes (use join to avoid array ref issues)

  return {
    chartDataMap,
    isLoading,
    error,
  };
};
